import { Logo } from '@/components/shared/logo';
import { SidebarMain } from '@/components/layout/sidebar-main';
import { SidebarUser } from '@/components/layout/sidebar-user';
import { CreditPackagesDialog } from '@/components/pricing/public-credit-packages';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { websiteConfig } from '@/config/website';
import { Routes } from '@/lib/routes';
import { getLocale, getLocaleDir, localizeHref } from '@/lib/locale';
import type { SessionUser } from '@/auth/types';
import { IconShoppingCart } from '@tabler/icons-react';
import { useState, type ComponentProps } from 'react';
import { m } from '@/locale/paraglide/messages';

type DashboardSidebarProps = ComponentProps<typeof Sidebar> & {
  user?: SessionUser | null;
};

/**
 * Dashboard sidebar
 */
export function DashboardSidebar({
  user = null,
  side,
  ...props
}: DashboardSidebarProps) {
  const { isMobile, setOpenMobile } = useSidebar();
  const locale = getLocale();
  const homeHref = localizeHref(Routes.Root, { locale });
  const resolvedSide =
    side ?? (getLocaleDir(locale) === 'rtl' ? 'right' : 'left');

  const closeMobileSidebar = () => {
    if (isMobile) setOpenMobile(false);
  };

  return (
    <Sidebar collapsible="icon" side={resolvedSide} {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              render={
                <a href={homeHref} onClick={closeMobileSidebar}>
                  <Logo className="size-9" />
                  <span className="truncate font-semibold text-base">
                    {websiteConfig.metadata?.name}
                  </span>
                </a>
              }
              className="h-12 data-[slot=sidebar-menu-button]:!p-1.5"
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarMain />
      </SidebarContent>

      <SidebarFooter className="flex flex-col gap-4">
        <SidebarUpgradeCard onActivate={closeMobileSidebar} />
        <SidebarUser user={user} />
      </SidebarFooter>
    </Sidebar>
  );
}

function SidebarUpgradeCard({ onActivate }: { onActivate: () => void }) {
  const locale = getLocale();
  const [creditsDialogOpen, setCreditsDialogOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => {
          onActivate();
          setCreditsDialogOpen(true);
        }}
        className="group-data-[collapsible=icon]:hidden inline-flex h-12 items-center gap-2 rounded-full border border-[#e2b84e]/70 bg-[#fff1d2] px-3 text-[#17130a] shadow-[0_10px_26px_rgba(212,175,55,0.22)] transition hover:border-[#f0d46c] hover:bg-[#fff6df] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d4af37]/55"
      >
        <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-white text-[#d4a012] shadow-inner ring-1 ring-[#f2d27a]/70">
          <IconShoppingCart className="size-4" />
        </span>
        <span className="min-w-0 truncate font-semibold text-sm">
          {m.settings_billing_card_upgrade_plan({}, { locale })}
        </span>
      </button>
      <CreditPackagesDialog
        checkoutMetadata={{ source: 'sidebar_upgrade' }}
        open={creditsDialogOpen}
        onOpenChange={setCreditsDialogOpen}
      />
    </>
  );
}
